import { useEffect, useState } from 'react';

import { useHistory } from 'react-router';
import { PATHS, useIsClientReady } from '@belong/common';
import classNames from 'classnames/bind';
import Button, { BUTTON_TYPES } from 'components/Button/Button';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import DocumentCard from 'components/DocumentCard/DocumentCard';
import Field from 'components/Field/Field';
import { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import IconButton from 'components/IconButton/IconButton';
import ModalV2 from 'components/Modal/ModalV2/ModalV2';
import PDFViewer from 'components/PDFViewer/PDFViewer';
import TabBar from 'components/TabBar/TabBar';
import { Text } from 'design-system';
import { LeaseRenderer } from 'lease-signing-flow-v2/components/lease-renderer/lease-renderer';
import { LEASE_SIGNING_FLOW_STRINGS } from 'lease-signing-flow-v2/constants/strings';
import AgreementFlowFormLayout from 'pages/AgreementFlow/AgreementFlowFormLayout/AgreementFlowFormLayout';
import { fetchAgreementPdfUrl } from 'store/redux/agreement-flow/actions';
import { requireTrue } from 'utils/validation';
import { SeattleSignStep } from './seattle-sign-step';
import styles from './sign-step.module.css';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

const cx = classNames.bind(styles);

const tabItems = [
  {
    text: `Lease`,
    key: 0,
  },
  {
    text: `Addendums`,
    key: 1,
  },
];

const initialValues = {};

type Props = {
  currentStep?: any;
  currentStepFormData?: unknown;
  home: {
    leaseOffered?: {
      renewalNumber?: number;
    };
  };
  lease?: {
    leaseInfo?: {
      residents?: unknown[];
    };
  };
  onSubmit: (values?: any) => void;
  setLoading: (isLoading: boolean) => void;
  setSidebarTip: () => void;
  openTipModal: () => void;
};

export const SignStep = ({
  currentStep,
  home,
  lease,
  onSubmit,
  setLoading,
  setSidebarTip,
  openTipModal,
  ...rest
}: Props) => {
  const history = useHistory();
  const isClientReady = useIsClientReady();
  // `tabItems` is a module-level literal with two entries, so both index reads
  // here and below always find one.
  const [selectedTab, setSelectedTab] = useState(tabItems[0]!);
  const [currentPdf, setCurrentPdf] = useState<string | null>(null);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  // Get sign lease from currentStep prop
  const offeredLease = currentStep?.signLease;

  useEffect(() => {
    // Check for roommate signing error if provided in currentStep
    if (currentStep?.error?.errorCode === 'PRIMARY_MUST_SIGN_FIRST') {
      history.push(`${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}`);
    }
  }, [currentStep, history]);

  const handleDownloadClick = async () => {
    setLoading(true);

    try {
      const response = await dispatch(
        fetchAgreementPdfUrl(offeredLease?.leaseDocumentInstance?.uniqueId),
      );

      if (response.url) {
        window.open(response.url, '_blank');
      }
    } catch (e) {
      console.error(e);
    }

    setLoading(false);
  };

  const handleSubmit = async () => {
    onSubmit();
  };

  const handleSelectedTab = (tab: any) => {
    setSelectedTab(tab);
  };

  function handleClosePDFModal() {
    setIsPDFModalOpen(false);
    setCurrentPdf(null);
  }

  if (!offeredLease) {
    return null;
  }

  if (offeredLease?.signMode === 'External') {
    return (
      <SeattleSignStep
        handleSubmit={handleSubmit}
        handleDownload={handleDownloadClick}
        residents={lease?.leaseInfo?.residents?.length || 1}
        {...rest}
      />
    );
  }

  return (
    <>
      <AgreementFlowFormLayout
        {...rest}
        onSubmit={handleSubmit}
        title={
          (home.leaseOffered?.renewalNumber ?? 0) > 0
            ? LEASE_SIGNING_FLOW_STRINGS['sign.title-renewal']
            : LEASE_SIGNING_FLOW_STRINGS['sign.title']
        }
        subTitle={LEASE_SIGNING_FLOW_STRINGS['sign.subtitle']}
        initialValues={initialValues}
        ctaProps={{ label: LEASE_SIGNING_FLOW_STRINGS['sign.CTA'] }}
        getForm={() => {
          return (
            <div className={cx('form-container')}>
              <div className={cx('row')}>
                <TabBar
                  name="lease-tabbar"
                  items={tabItems}
                  onSelect={handleSelectedTab}
                  selected={selectedTab.key}
                />
                <div className={cx('download-container')}>
                  <div className={cx('desktop')}>
                    <Button
                      onClick={handleDownloadClick}
                      buttonType={BUTTON_TYPES.TEXT}
                      label={LEASE_SIGNING_FLOW_STRINGS['sign.download']}
                    />
                  </div>
                  <div className={cx('mobile')}>
                    <IconButton
                      icon={GENERAL_ICONS.DOWNLOAD}
                      onClick={handleDownloadClick}
                      scale={1}
                    />
                  </div>
                </div>
              </div>
              <div className={cx('agreement-container')}>
                {selectedTab.key === tabItems[0]!.key && (
                  <LeaseRenderer
                    agreement={offeredLease?.leaseDocumentInstance?.html}
                    setTip={setSidebarTip}
                    openTipModal={openTipModal}
                  />
                )}
                {selectedTab.key === tabItems[1]!.key && (
                  <div className={cx('addendums-container')}>
                    {offeredLease?.addendums?.map((addendum: any) => (
                      <DocumentCard
                        bordered={false}
                        size={DocumentCard.SIZES.SMALL}
                        key={addendum.url}
                        onClick={() => {
                          setCurrentPdf(addendum.url);
                          setIsPDFModalOpen(true);
                        }}
                        label={<Text>{addendum.documentType}</Text>}
                        type={addendum.mediaType}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className={cx('sign-checkbox-container')}>
                <Field
                  name="agreeToElectronicSign"
                  component={CheckboxFinalFormAdapter}
                  label={LEASE_SIGNING_FLOW_STRINGS['sign.checkbox']}
                  validate={requireTrue}
                  firstSection
                />
              </div>
            </div>
          );
        }}
      />
      {isClientReady && (
        <ModalV2
          backdropTheme="dark"
          show={isPDFModalOpen}
          onHide={handleClosePDFModal}
          type="black-background"
          noPadding
        >
          <PDFViewer file={currentPdf} fullScreen />
        </ModalV2>
      )}
    </>
  );
};
